import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createColumnHelper } from '@tanstack/react-table'
import _ from 'lodash'
import { Download, Trash2 } from 'lucide-react'
import PropTypes from 'prop-types'
import { useCallback, useEffect, useState } from 'react'

import { ActionButtons } from '@/components/ActionButtons'
import { DataTable } from '@/components/DataTable'
import { SortableColumnHeader } from '@/components/DataTable/DataTable'
import { DataTableFilter } from '@/components/DataTable/DataTableFilter'
import { Button } from '@/components/elements/button'
import { deleteDocument, updateDocument } from '@/services/documents.service'
import { formatTimeAndDate } from '@/utils/formatters'
import { toast } from '@/utils/helpers'
import { formatUserName } from '@/utils/userHelpers'

import { ConfirmButton } from '../ConfirmButton'
import { StatusBadge } from '../StatusBadge'

export default function DocumentsTable({
  getDocuments,
  filters = [],
  extraQueryKeys = {},
  showAccount = false,
  showOwner = false,
  showType = true,
  actions = [],
  // Approve | Edit | null
  approvalColumnType = null,
  canApprove = false,
  setEditingDocument,
}) {
  // State
  const [sorting, setSorting] = useState([{ id: 'created_at', desc: true }])
  const [pagination, setPagination] = useState({
    pageSize: 20,
    pageIndex: 0,
  })

  const [filterValues, setFilterValues] = useState({})

  useEffect(() => {
    setPagination((prev) => ({ ...prev, pageIndex: 0 }))
  }, [filterValues])

  const handleError = (m) => toast(m, 'error')
  const handleSuccess = (m) => toast(m, 'success')
  const queryClient = useQueryClient()
  const { isPending: isApproving, mutate: approveDocument } = useMutation({
    mutationFn: (data) => updateDocument(data.id, { isApproved: true }),
    onError: (err) => handleError(err.message),
    onSuccess: (data) => {
      handleSuccess(`Document ${data.name} approved successfully`)

      // Refresh documents list
      queryClient.invalidateQueries({ queryKey: ['accounts', 'documents'] })
    },
  })
  const { isPending: isDeleting, mutate: removeDocument } = useMutation({
    mutationFn: (data) => deleteDocument(data.id),
    onError: (err) => handleError(err.message),
    onSuccess: () => {
      handleSuccess('Document deleted successfully')
      queryClient.invalidateQueries({ queryKey: ['accounts', 'documents'] })
    },
  })

  const { data, isPending } = useQuery({
    queryKey: ['accounts', 'documents', sorting, pagination, filterValues, extraQueryKeys],
    queryFn: () =>
      getDocuments({
        sorting,
        pagination,
        searchTerm: filterValues.search,
        filters: _.omit(filterValues, 'search'),
      }),
    placeholderData: keepPreviousData,
  })

  // Table setup
  const downloadRenderer = useCallback(
    (info) => (
      <a
        href={info.getValue()}
        target="_blank"
        rel="noreferrer"
        className="text-primary hover:text-foreground"
      >
        <Download />
        <span className="sr-only">Download</span>
      </a>
    ),
    [],
  )
  const nameHeaderRenderer = useCallback(
    ({ column }) => <SortableColumnHeader column={column} label="Name" />,
    [],
  )
  const createdAtHeaderRenderer = useCallback(
    ({ column }) => <SortableColumnHeader column={column} label="Created At" />,
    [],
  )
  const deleteRenderer = useCallback(
    ({ row }) => (
      <ConfirmButton
        confirmTitle="Delete Document"
        confirmContent={`Are you sure you want to delete "${row.original.name}"? This action cannot be undone.`}
        className="place-self-end"
        variant="destructive"
        size="sm"
        loading={isDeleting}
        onClick={() => removeDocument(row.original)}
      >
        <Trash2 className="h-4 w-4" />
        <span className="sr-only">Delete document</span>
      </ConfirmButton>
    ),
    [removeDocument, isDeleting],
  )
  const isApprovedRenderer = useCallback(
    ({ getValue, row }) => {
      if (row.original.type !== 'Upload') return null
      const isApproved = getValue()
      if (approvalColumnType === 'Approve') {
        if (isApproved) return <StatusBadge status="Approved" type="documentApproval" />
        if (!canApprove) return <StatusBadge status="Pending" type="documentApproval" />
        return (
          <ConfirmButton
            confirmTitle="Approve Document"
            confirmContent="Are you sure you want to approve this document?"
            variant="outline"
            size="sm"
            loading={isApproving}
            onClick={() => approveDocument(row.original)}
          >
            Approve
          </ConfirmButton>
        )
      }
      if (approvalColumnType === 'Edit') {
        if (isApproved) return <StatusBadge status="Approved" type="documentApproval" />
        return (
          <div className="flex flex-row items-center gap-2">
            <StatusBadge status="Pending" type="documentApproval" />
            <Button variant="default" size="sm" onClick={() => setEditingDocument(row.original)}>
              Update
            </Button>
          </div>
        )
      }
      return null
    },
    [approvalColumnType, canApprove, approveDocument, setEditingDocument, isApproving],
  )

  const columnHelper = createColumnHelper()
  const columns = [
    ...(showOwner
      ? [
          columnHelper.accessor((row) => formatUserName(row.owner), {
            id: 'owner',
            header: 'Owner',
          }),
        ]
      : []),
    columnHelper.accessor('name', {
      header: nameHeaderRenderer,
      enableSorting: true,
    }),
    ...(showAccount
      ? [
          columnHelper.accessor((row) => row.entity.name, {
            id: 'account',
            header: 'Account',
          }),
        ]
      : []),
    ...(showType
      ? [
          columnHelper.accessor('type', {
            header: 'Type',
          }),
        ]
      : []),
    columnHelper.accessor('createdAt', {
      // Sorting key has to match the API. Since it's passed as the value, the
      // convertOutgoingData function does not convert it.
      id: 'created_at',
      header: createdAtHeaderRenderer,
      cell: (info) => formatTimeAndDate(info.getValue()),
    }),
    columnHelper.accessor('signedFilePath', {
      header: 'Download',
      cell: downloadRenderer,
    }),
    ...(approvalColumnType
      ? [
          columnHelper.accessor('isApproved', {
            header: 'Approval',
            cell: isApprovedRenderer,
          }),
        ]
      : []),
    ...(approvalColumnType
      ? [
          columnHelper.display({
            id: 'delete',
            cell: deleteRenderer,
            size: 80,
          }),
        ]
      : []),
  ]
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-row flex-wrap content-end justify-between">
        <DataTableFilter
          setFilters={setFilterValues}
          filterValues={filterValues}
          filters={filters}
        />
        <ActionButtons actions={actions} className="flex-wrap content-end" />
      </div>

      <DataTable
        columns={columns}
        data={data?.results || []}
        loading={isPending}
        pagination={pagination}
        rowCount={data?.count || 0}
        setPagination={setPagination}
        sorting={sorting}
        setSorting={setSorting}
      />
    </div>
  )
}

DocumentsTable.propTypes = {
  getDocuments: PropTypes.func.isRequired,
  filters: PropTypes.array,
  extraQueryKeys: PropTypes.object,
  showAccount: PropTypes.bool,
  showOwner: PropTypes.bool,
  showType: PropTypes.bool,
  actions: PropTypes.array,
  approvalColumnType: PropTypes.oneOf(['Approve', 'Edit', null]),
  canApprove: PropTypes.bool,
  setEditingDocument: PropTypes.func,
}
